import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bank, ArrowRight, Check, Truck, Bed, Compass, Scales, UsersThree, CurrencyEur,
  Buildings, HouseLine, Warehouse, Storefront, PaperPlaneTilt, SunHorizon, ChartLineUp, Boat, Briefcase,
} from "@phosphor-icons/react";
import { useT } from "../i18n";
import { Reveal } from "../components/Reveal";
import PropertyCard from "../components/PropertyCard";
import MonthlySimulator from "../components/MonthlySimulator";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const HERO = "https://images.unsplash.com/photo-1559845877-23f2566a9975?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const PARTNER_IMG = "https://images.unsplash.com/photo-1519359810479-37e924ddd0b4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const LOGISTICS_IMG = "https://images.unsplash.com/photo-1504150558240-0b4fd8946624?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const TEAM_IMG = "https://images.unsplash.com/photo-1779700210487-a01758a3c55a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const Home = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/properties", { params: { featured: true } }).then(({ data }) => setFeatured(data.slice(0, 6))).catch(() => {});
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const services = [
    { icon: Truck, t: t("services.transport_t"), d: t("services.transport_d") },
    { icon: Bed, t: t("services.stay_t"), d: t("services.stay_d") },
    { icon: Compass, t: t("services.tour_t"), d: t("services.tour_d") },
    { icon: Scales, t: t("services.legal_t"), d: t("services.legal_d") },
    { icon: UsersThree, t: t("services.team_t"), d: t("services.team_d") },
    { icon: CurrencyEur, t: t("services.finance_t"), d: t("services.finance_d") },
  ];

  const typeCards = [
    { icon: Buildings, key: "new_build" },
    { icon: HouseLine, key: "renovation" },
    { icon: Warehouse, key: "logistics" },
    { icon: Storefront, key: "business_premises" },
  ];

  const profiles = [
    { icon: SunHorizon, title: t("profiles.retirement_t"), desc: t("profiles.retirement_d"), link: "/biens" },
    { icon: HouseLine, title: t("profiles.secondary_t"), desc: t("profiles.secondary_d"), link: "/biens?type=new_build" },
    { icon: ChartLineUp, title: t("profiles.rental_t"), desc: t("profiles.rental_d"), link: "/biens" },
    { icon: Boat, title: t("profiles.logistics_t"), desc: t("profiles.logistics_d"), link: "/biens?type=logistics" },
    { icon: Briefcase, title: t("profiles.business_t"), desc: t("profiles.business_d"), link: "/biens?type=business_premises" },
    { icon: CurrencyEur, title: t("profiles.budget_t"), desc: t("profiles.budget_d"), link: "/cout-de-la-vie" },
  ];

  return (
    <div className="relative z-10">
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-[92vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Immobilier Maroc" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-gradient" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-20 pt-32 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 bg-white/95 backdrop-blur-md border border-white/20 px-4 py-2 rounded-full text-navy text-sm mb-6 shadow-lg">
              <img src="/cam-logo.jpg" alt="Crédit Agricole du Maroc" className="h-5 w-auto object-contain" /> <span className="font-medium">{t("hero.badge")}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-[1.08] tracking-tight">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-xl leading-relaxed">{t("hero.subtitle")}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <button data-testid="hero-cta-explore" onClick={() => navigate("/biens")} className="group inline-flex items-center justify-center gap-2 bg-gold text-navy font-semibold px-7 py-4 rounded-full hover:bg-[#C8924A] transition-colors">
                {t("hero.cta_explore")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform rtl:rotate-180" />
              </button>
              <button data-testid="hero-cta-simulate" onClick={() => scrollTo("simulator")} className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/25 text-white font-semibold px-7 py-4 rounded-full hover:bg-white/20 transition-colors">
                {t("hero.cta_simulate")}
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }} className="mt-16 grid grid-cols-3 gap-6 max-w-xl border-t border-white/15 pt-8">
            <Stat n="50+" l={t("hero.stat_projects")} />
            <Stat n="6" l={t("hero.stat_cities")} />
            <Stat n="A→Z" l={t("hero.stat_support")} />
          </motion.div>
        </div>
      </section>

      {/* TYPE STRIP */}
      <section className="bg-navy relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10 rtl:divide-x-reverse">
          {typeCards.map((c, i) => (
            <button key={c.key} onClick={() => navigate(`/biens?type=${c.key}`)} data-testid={`type-strip-${c.key}`}
              className="group flex items-center gap-3 py-7 px-5 text-white/80 hover:text-gold hover:bg-white/[0.03] transition-colors">
              <c.icon size={28} weight="light" className="text-gold shrink-0" />
              <span className="text-sm font-medium text-start leading-tight">{t(`types.${c.key}`)}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ACCROCHE */}
      <section data-testid="hook-band" className="bg-gold relative z-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5 text-center sm:text-start">
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex w-12 h-12 rounded-full bg-navy/10 items-center justify-center shrink-0"><CurrencyEur size={26} weight="light" className="text-navy" /></span>
            <p className="font-display text-2xl sm:text-3xl text-navy font-medium leading-tight">{t("hook.title")}</p>
          </div>
          <button data-testid="hook-cta" onClick={() => scrollTo("simulator")} className="shrink-0 inline-flex items-center gap-2 bg-navy text-white font-semibold px-7 py-3.5 rounded-full hover:bg-[#254B75] transition-colors">
            {t("hook.cta")} <ArrowRight size={18} className="rtl:rotate-180" />
          </button>
        </div>
      </section>

      {/* PROFILES */}
      <section id="profils" data-testid="profiles-section" className="bg-white relative z-10 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy font-light tracking-tight mb-3">{t("profiles.title")}</h2>
            <p className="text-muted-foreground">{t("profiles.subtitle")}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06}>
                <button onClick={() => navigate(p.link)} data-testid={`profile-${i}`} className="group text-start w-full h-full bg-background border border-navy/10 hover:border-gold rounded-2xl p-7 transition-colors">
                  <p.icon size={38} weight="light" className="text-gold mb-4" />
                  <h3 className="font-display text-xl text-navy mb-2">{p.title}</h3>
                  <p className="text-sm text-muted-foreground">{p.desc}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-navy group-hover:text-gold">{t("profiles.explore")} <ArrowRight size={14} className="rtl:rotate-180" /></span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNER */}
      <section className="bg-background relative z-10 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="overline text-gold">{t("partner.overline")}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy font-light tracking-tight mt-4 mb-6">{t("partner.title")}</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">{t("partner.desc")}</p>
            <ul className="space-y-4">
              {[t("partner.point1"), t("partner.point2"), t("partner.point3")].map((p) => (
                <li key={p} className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gold/15 flex items-center justify-center shrink-0"><Check size={14} weight="bold" className="text-gold" /></span>
                  <span className="text-navy font-medium">{p}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img src={PARTNER_IMG} alt="Crédit Agricole du Maroc" className="w-full aspect-[4/3] object-cover" />
              <div className="absolute -bottom-6 -start-6 rtl:-end-6 rtl:start-auto bg-white p-5 max-w-[260px] shadow-xl hidden sm:block rounded-2xl border border-navy/10">
                <img src="/cam-logo.jpg" alt="Crédit Agricole du Maroc" className="h-12 w-auto object-contain" />
                <div className="text-xs text-muted-foreground mt-3">{t("hero.badge")}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FEATURED */}
      <section className="bg-white relative z-10 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
            <div>
              <span className="overline text-gold">{t("featured.overline")}</span>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy font-light tracking-tight mt-4">{t("featured.title")}</h2>
              <p className="text-muted-foreground mt-3 max-w-xl">{t("featured.subtitle")}</p>
            </div>
            <button data-testid="featured-viewall" onClick={() => navigate("/biens")} className="group inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors whitespace-nowrap">
              {t("featured.viewall")} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform rtl:rotate-180" />
            </button>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}><PropertyCard property={p} index={i} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="bg-background relative z-10 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl mb-14">
            <span className="overline text-gold">{t("services.overline")}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy font-light tracking-tight mt-4 mb-3">{t("services.title")}</h2>
            <p className="text-muted-foreground">{t("services.subtitle")}</p>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
            {services.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.05}>
                <div className="bg-background h-full p-8 group hover:bg-white transition-colors">
                  <s.icon size={36} weight="light" className="text-gold mb-5" />
                  <h3 className="font-display text-xl text-navy mb-2">{s.t}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-10 relative overflow-hidden">
              <img src={LOGISTICS_IMG} alt="Logistique" className="w-full h-56 sm:h-72 object-cover" />
              <div className="absolute inset-0 bg-navy/50 flex items-center">
                <div className="px-8 sm:px-12 max-w-lg">
                  <div className="font-display text-2xl sm:text-3xl text-white">« {t("services.title")} »</div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SIMULATOR */}
      <MonthlySimulator />

      {/* TEAM */}
      <section className="bg-white relative z-10 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal delay={0.1} className="order-2 lg:order-1">
            <img src={TEAM_IMG} alt="Équipe YADIMMO" className="w-full aspect-[4/3] object-cover" />
          </Reveal>
          <Reveal className="order-1 lg:order-2">
            <span className="overline text-gold">{t("team.overline")}</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-navy font-light tracking-tight mt-4 mb-6">{t("team.title")}</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">{t("team.subtitle")}</p>
            <div className="grid grid-cols-2 gap-6">
              {[
                { r: t("services.legal_t"), c: "🇫🇷 France" },
                { r: "Notaire & Avocat", c: "🇲🇦 Maroc" },
                { r: "Maîtres d'œuvre", c: "🇲🇦 Maroc" },
                { r: "Conseil investisseur", c: "🇫🇷 France" },
              ].map((m) => (
                <div key={m.r} className="border-s-2 border-gold ps-4 py-1">
                  <div className="font-display text-lg text-navy">{m.r}</div>
                  <div className="text-sm text-muted-foreground">{m.c}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT */}
      <ContactSection />
    </div>
  );
};

const Stat = ({ n, l }) => (
  <div>
    <div className="font-display text-3xl sm:text-4xl text-gold">{n}</div>
    <div className="text-xs text-white/70 mt-1 uppercase tracking-wider">{l}</div>
  </div>
);

export const ContactSection = ({ propertyId = null, defaultSubject = "general" }) => {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...form, property_id: propertyId, subject: defaultSubject });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-navy relative z-10 py-24 sm:py-32">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="overline text-gold">{t("contact.overline")}</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight mt-4 mb-4">{t("contact.title")}</h2>
          <p className="text-white/60 max-w-md">{t("contact.subtitle")}</p>
        </div>
        <form data-testid="contact-form" onSubmit={submit} className="space-y-4">
          <Input data-testid="contact-name" required placeholder={t("contact.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white/5 border-white/15 text-white placeholder:text-white/40 h-12 rounded-lg" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="contact-email" required type="email" placeholder={t("contact.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white/5 border-white/15 text-white placeholder:text-white/40 h-12 rounded-lg" />
            <Input data-testid="contact-phone" placeholder={t("contact.phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white/5 border-white/15 text-white placeholder:text-white/40 h-12 rounded-lg" />
          </div>
          <Textarea data-testid="contact-message" required rows={4} placeholder={t("contact.message")} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white/5 border-white/15 text-white placeholder:text-white/40 rounded-lg" />
          <button data-testid="contact-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy font-semibold py-4 rounded-full hover:bg-[#C8924A] transition-colors disabled:opacity-60">
            <PaperPlaneTilt size={18} weight="light" /> {loading ? t("common.loading") : t("contact.send")}
          </button>
        </form>
      </div>
    </section>
  );
};

export default Home;
